import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import './CardDetailsForm.css';

interface CardDetailsPopupProps {
  onCancel: () => void;
  onSubmitPaymentMethod: (paymentMethodId: string) => void;
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

const stripeElementStyle = {
  style: {
    base: {
      fontSize: '16px',
      color: '#32325d',
      '::placeholder': {
        color: '#a0aec0',
      },
    },
    invalid: {
      color: '#32325d',
      iconColor: '#32325d',
    },
  },
  autoComplete: 'off',
};

function InnerCardDetailsPopup({ onCancel, onSubmitPaymentMethod }: CardDetailsPopupProps) {

    const [cardBrand, setCardBrand] = useState<string | null>(null);
    const stripe = useStripe();
    const elements = useElements();

    const [nameOnCard, setNameOnCard] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
        document.body.style.overflow = '';
        };
    }, []);

    const handleSubmit = async () => {
        setError(null);

        if (!stripe || !elements) {
        setError("Stripe has not loaded yet.");
        return;
        }

        const cardNumberElement = elements.getElement(CardNumberElement);
        if (!cardNumberElement) {
        setError("Card number element not found.");
        return;
        }

        setLoading(true);

        const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
            name: nameOnCard,
        },
        });

        if (error) {
        setError(error.message || "Failed to create payment method.");
        setLoading(false);
        return;
        }

        if (!paymentMethod) {
        setError("No payment method created.");
        setLoading(false);
        return;
        }

        onSubmitPaymentMethod(paymentMethod.id);
        setLoading(false);
    };

    return (
        <div className="cdp-overlay">
        <div className="cdp-container">
            <div className="cdp-header">Update Card Details</div>
            <div className="cdp-body">
            <div className="cdp-field-group cdp-full-width">
                <label className="cdp-label" htmlFor="nameOnCard">Name on Card</label>
                <input
                id="nameOnCard"
                className="cdp-input"
                value={nameOnCard}
                onChange={(e) => setNameOnCard(e.target.value)}
                placeholder="Full name as on card"
                />
            </div>

            <div className="cdp-field-group cdp-full-width">
                <label className="cdp-label">Card Number</label>
                {cardBrand && (
                    <div className="cdp-card-brand">
                        {cardBrand.charAt(0).toUpperCase() + cardBrand.slice(1)}
                    </div>
                )}
                <div className="cdp-stripe-element">
                    <CardNumberElement
                        options={stripeElementStyle}
                        onChange={(event) => {
                            if (event.brand && event.brand !== 'unknown') {
                            setCardBrand(event.brand);
                            } else {
                            setCardBrand(null);
                            }
                        }}
                />
                </div>
            </div>

            <div className="cdp-field-row">
                <div className="cdp-field-group cdp-half-width">
                <label className="cdp-label">Expiration Date</label>
                <div className="cdp-stripe-element">
                    <CardExpiryElement options={stripeElementStyle} />
                </div>
                </div>
                <div className="cdp-field-group cdp-half-width">
                <label className="cdp-label">CVC</label>
                <div className="cdp-stripe-element">
                    <CardCvcElement options={stripeElementStyle} />
                </div>
                </div>
            </div>

            {error && <div className="cdp-error">{error}</div>}

            <div className="cdp-actions">
                <button className="cdp-btn cdp-cancel" onClick={onCancel} disabled={loading}>Cancel</button>
                <button className="cdp-btn cdp-submit" onClick={handleSubmit} disabled={!stripe || loading}>
                {loading ? "Saving..." : "Submit"}
                </button>
            </div>
            </div>
        </div>
        </div>
    );
}

export default function CardDetailsPopup(props: CardDetailsPopupProps) {
  return (
    <Elements stripe={stripePromise}>
      <InnerCardDetailsPopup {...props} />
    </Elements>
  );
}